<script setup lang="ts">
import {reactive, ref, watch} from "vue";
import {watchDebounced} from "@vueuse/core";
import {useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {deleteContact, getContacts as apiGetContacts} from "@/Services/Admin/admin-contacts";
import {getting} from "@/Composables/api";
import {Avatar, Button, Card, Icon, IconField, InputIcon, InputText, MultiSelect} from "@/Components/Library";
import {PageLoading} from "@/Components/Global";
import AddContactDialog from "@/Pages/Admin/Contacts/AddContactDialog.vue";
import {useMainStore} from "@/Stores/store";

const $store = useMainStore();
const confirm = useConfirm();
const router = useRouter();

const props = defineProps<{
    user: any;
}>();

const state = reactive({
    show: {
        addContacts: false,
    },
    filters: {
        invitation_status: [],
    },
});
const search = ref(null);

const contacts = ref([]);

const getContacts = async () => {
    apiGetContacts({
        filter: {
            search: search.value,
            user_id: props.user.id,
            invitation_status: state.filters.invitation_status,
        },
        include: ["person.profile.files"],
    }).then(({data}) => (contacts.value = data));
};
getContacts();

const archiveContact = async (contactId: string) => {
    confirm.require({
        message: "Are you sure you want to archive this contact?",
        accept: async () => {
            await deleteContact(contactId);
            $store.toastMessages.push({
                severity: "success",
                summary: "Removed contact",
                detail: "The contact has been archived successfully.",
                life: 3000,
            });
            contacts.value = contacts.value.filter((contact) => contact.id !== contactId);
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

const handleFilter = () => {
    router.push({
        query: {
            ...router.currentRoute.value.query,
            contacts: {
                filter: {
                    search: search.value,
                    invitation_status: state.filters.invitation_status,
                },
            },
        },
    });
    getContacts();
};

watch(
    state.filters,
    () => {
        if (search.value !== router.currentRoute.value.query.contacts.filter.search) {
            return;
        }

        handleFilter();
    },
    {deep: true},
);

watchDebounced(search, handleFilter, {debounce: 500, deep: true});
</script>
<template>
    <div>
        <Card>
            <template #title>
                <div class="mb-2 flex items-end gap-2">
                    <span>Contacts</span>
                    <div class="ml-auto flex justify-between gap-2">
                        <Button
                            class="h-10 w-10 border bg-stone-100 hover:bg-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
                            :icon="{
                                icon: 'mdi:plus',
                                width: 24,
                            }"
                            @click="
                                () => {
                                    console.log('add contact');
                                    state.show.addContacts = true;
                                }
                            "
                        />
                    </div>
                </div>
            </template>
            <div class="mb-4 flex flex-col items-center gap-2 lg:flex-row">
                <!-- Search -->
                <IconField class="w-full grow">
                    <InputIcon>
                        <Icon icon="prime:search" />
                    </InputIcon>
                    <InputText
                        id="search_contacts"
                        v-model="search"
                        placeholder="Search contacts..."
                        class="!py-1.5 pl-10"
                        fluid
                        type="search"
                    />
                    <InputIcon class="prime:times-circle" @click="search = null" />
                </IconField>
                <!-- Filters -->
                <div class="flex w-full lg:max-w-60">
                    <MultiSelect
                        class="w-full grow"
                        v-model="state.filters.invitation_status"
                        placeholder="Filter by status"
                        :options="[
                            {label: 'Accepted', value: 'accepted'},
                            {label: 'Pending', value: 'pending'},
                            {label: 'Declined', value: 'declined'},
                        ]"
                        option-label="label"
                        option-value="value"
                        pt:label:class="px-2 py-1.5"
                        pt:dropdown:class="flex items-center pr-2"
                        pt:header:class="hidden"
                    />
                </div>
            </div>
            <PageLoading v-if="getting.includes('contacts')" />
            <div
                v-if="!getting.includes('contacts') && contacts?.length"
                class="flex flex-col divide-y dark:divide-stone-700"
            >
                <template v-for="contact in contacts" :key="contact.id">
                    <div
                        class="relative flex w-full transition-colors duration-300 hover:bg-stone-50 dark:hover:bg-stone-950"
                    >
                        <component
                            :is="contact.person_id ? 'RouterLink' : 'div'"
                            RouterLink
                            class="grow"
                            :to="{name: 'admin.users.show', params: {user: contact.person_id}}"
                        >
                            <div class="flex w-full items-center gap-2 py-2.5 pr-2">
                                <div class="flex grow items-center gap-2">
                                    <Avatar :user="contact" class="h-10 w-10 shrink-0 !rounded-full" />
                                    <h3 class="grow font-normal">
                                        {{ contact.first_name }}
                                        <span class="font-bold">{{ contact.last_name }}</span>
                                    </h3>
                                </div>
                            </div>
                        </component>
                        <div class="flex items-stretch">
                            <Button
                                class="rounded-none !pl-1 !pr-4 hover:bg-stone-200 dark:hover:bg-stone-700"
                                :icon="{
                                    icon: 'mdi:close',
                                    width: 20,
                                }"
                                @click="archiveContact(contact.id)"
                            />
                        </div>
                    </div>
                </template>
            </div>
            <div v-else>No contacts found.</div>
        </Card>
        <AddContactDialog v-model:visible="state.show.addContacts" :currentUser="user" @added="getContacts" />
    </div>
</template>
<style scoped></style>
